import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { User, Mail, Phone, Lock, Gift, ArrowRight } from "lucide-react";
import { useLang, LanguageSwitcher } from "@/lib/lang";

export default function ClientRegister() {
  const { t } = useLang();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({
    name: "", email: "", phone: "", password: "",
    referral_code: (params.get("ref") || "").toUpperCase(),
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) { toast.error(t("toast_error_password_short")); return; }
    setLoading(true);
    try {
      await api.post("/client/register", f);
      toast.success(t("toast_otp_email_sent"));
      nav(`/client/verify?email=${encodeURIComponent(f.email)}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="client-register-page">
        <div className="flex justify-end mb-4">
          <LanguageSwitcher testId="register-lang-switch" className="text-slate-300" />
        </div>
        <div className="text-center mb-8">
          <div className="inline-flex w-14 h-14 border border-[#f59e0b] items-center justify-center mb-4">
            <span className="font-display text-[#f59e0b] text-3xl">D</span>
          </div>
          <h1 className="font-display text-3xl text-white">{t("client_register_title")}</h1>
          <p className="text-slate-400 text-sm mt-2">{t("client_register_subtitle")}</p>
        </div>

        <form onSubmit={submit} className="bg-white p-8 border border-slate-200 space-y-4">
          <Field icon={User} label={t("client_field_name")}>
            <Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ana Silva" data-testid="reg-name" />
          </Field>
          <Field icon={Mail} label={t("client_field_email")}>
            <Input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="voce@exemplo.pt" data-testid="reg-email" />
          </Field>
          <Field icon={Phone} label={t("client_field_phone")}>
            <Input type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="+351 912 345 678" data-testid="reg-phone" />
          </Field>
          <Field icon={Lock} label={t("client_field_password")}>
            <Input required type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="••••••••" data-testid="reg-password" />
          </Field>
          <Field icon={Gift} label={t("client_field_referral")}>
            <Input value={f.referral_code} onChange={(e) => setF({ ...f, referral_code: e.target.value.toUpperCase() })} placeholder="EX. ANAC-3866" data-testid="reg-referral" />
          </Field>

          <button type="submit" disabled={loading} className="w-full h-11 bg-[#f59e0b] text-[#0a0f1e] font-bold text-sm uppercase tracking-wide hover:bg-[#f59e0b]/90 disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="reg-submit">
            {loading ? t("client_creating") : t("client_action_continue")} <ArrowRight className="w-4 h-4" />
          </button>

          <div className="text-center text-xs text-slate-500 pt-2">
            {t("client_have_account")} <Link to="/login" className="text-[#f59e0b] font-bold uppercase tracking-widest" data-testid="reg-to-login">{t("client_action_login")}</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-600 font-semibold flex items-center gap-1.5 mb-1.5">
        <Icon className="w-3 h-3" /> {label}
      </label>
      {children}
    </div>
  );
}
